import React from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Plus, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SeoAdmin() {
  const [pages, setPages] = React.useState([]);
  const [settings, setSettings] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [openId, setOpenId] = React.useState(null);
  const [drafts, setDrafts] = React.useState({});

  const load = () => api.adminPages().then(p => {
    setPages(p);
    const d = {};
    p.forEach(pg => { d[pg.id] = { ...(pg.seo_de || {}) }; });
    setDrafts(d);
  });
  React.useEffect(() => { load(); api.adminSettings().then(setSettings); }, []);

  const gen = async (id, lang) => {
    setBusyId(`${id}-${lang}`);
    try { await api.generateSeo(id, lang); toast.success(`SEO (${lang.toUpperCase()}) generiert`); await load(); }
    catch { toast.error("Fehler"); } finally { setBusyId(null); }
  };

  const saveSeo = async (id) => {
    try { await api.updatePage(id, { seo_de: drafts[id] }); toast.success("SEO gespeichert"); load(); }
    catch { toast.error("Fehler beim Speichern"); }
  };
  const setField = (id, k, v) => setDrafts(d => ({ ...d, [id]: { ...d[id], [k]: v } }));

  const saveSettings = async () => {
    try { await api.updateSettings(settings); toast.success("KI-Einstellungen gespeichert"); }
    catch { toast.error("Fehler"); }
  };
  const setS = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const setList = (k, i, v) => setSettings(s => { const a = [...(s[k] || [])]; a[i] = v; return { ...s, [k]: a }; });
  const addItem = (k) => setSettings(s => ({ ...s, [k]: [...(s[k] || []), ""] }));
  const delItem = (k, i) => setSettings(s => ({ ...s, [k]: (s[k] || []).filter((_, j) => j !== i) }));

  const links = [
    ["sitemap.xml", "/api/sitemap.xml"], ["robots.txt", "/api/robots.txt"],
    ["llms.txt", "/api/llms.txt"], ["structured-data.json", "/api/structured-data.json"],
  ];

  return (
    <div data-testid="admin-seo">
      <div className="mb-6"><div className="brut-tag mb-3">CMS / SEO & KI</div><h1 className="brut-h2">SEO & KI-Sichtbarkeit</h1>
        <p className="brut-body mt-3 max-w-2xl opacity-80">Alles für KI-Maschinen (ChatGPT, Perplexity, Google AI): strukturierte Daten, llms.txt, Crawler-Freigabe und pro Seite editierbare Meta-Texte.</p>
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          {links.map(([label, href]) => <a key={href} target="_blank" rel="noreferrer" className="font-mono underline" href={`${BACKEND}${href}`}>{label} ↗</a>)}
        </div>
      </div>

      {/* GLOBAL KI SETTINGS */}
      {settings && (
        <div className="border-2 border-[var(--ink)] p-5 mb-8 space-y-4" data-testid="seo-global">
          <div className="font-mono text-xs uppercase tracking-[0.2em]">Globale KI-Einstellungen</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="brut-label">Basis-URL (Domain)</label>
              <input className="brut-input" value={settings.public_base_url || ""} onChange={e => setS("public_base_url", e.target.value)} placeholder="https://dr-b-digitec.com" data-testid="seo-base-url" />
            </div>
            <label className="flex items-center gap-3 mt-7 font-mono text-xs uppercase">
              <input type="checkbox" checked={!!settings.ai_crawlers_allowed} onChange={e => setS("ai_crawlers_allowed", e.target.checked)} data-testid="seo-ai-toggle" />
              KI-Crawler erlauben (GPTBot, ClaudeBot, PerplexityBot, Google-Extended …)
            </label>
          </div>
          <div>
            <label className="brut-label">LLM-Intro (llms.txt — kurze Beschreibung des Unternehmens für KI)</label>
            <textarea rows={2} className="brut-textarea" value={settings.llms_intro_de || ""} onChange={e => setS("llms_intro_de", e.target.value)} data-testid="seo-llms-intro" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <ListEditor label="Profile / sameAs (URLs zu LinkedIn, Presse, Verzeichnissen)" k="same_as" arr={settings.same_as || []} setList={setList} add={addItem} del={delItem} />
            <ListEditor label="Themen / knowsAbout (Fachgebiete)" k="knows_about" arr={settings.knows_about || []} setList={setList} add={addItem} del={delItem} />
          </div>
          <button onClick={saveSettings} className="brut-btn !py-2 !px-4 text-xs" data-testid="seo-settings-save"><Save size={12} /> KI-Einstellungen speichern</button>
        </div>
      )}

      {/* PER-PAGE SEO */}
      <div className="font-mono text-xs uppercase tracking-[0.2em] mb-3">Seiten — Meta & KI-Zusammenfassung</div>
      <div className="space-y-2">
        {pages.map(p => {
          const d = drafts[p.id] || {};
          const open = openId === p.id;
          const problems = [!d.title && "Titel", !d.description && "Description", (d.description || "").length > 160 && "Desc >160", !d.ai_summary && "KI-TL;DR"].filter(Boolean);
          return (
            <div key={p.id} className="border-2 border-[var(--ink)]">
              <button onClick={() => setOpenId(open ? null : p.id)} className="w-full flex items-center justify-between p-4 text-left" data-testid={`seo-page-${p.slug}`}>
                <div><span className="font-mono text-xs opacity-60">/{p.slug}</span> <span className="font-semibold ml-2">{p.title_de}</span></div>
                <span className="font-mono text-[11px] uppercase">{problems.length ? <span className="text-red-600">{problems.join(", ")}</span> : <span className="opacity-50">vollständig</span>} · {open ? "−" : "+"}</span>
              </button>
              {open && (
                <div className="p-4 border-t-2 border-[var(--ink)] space-y-3">
                  <div>
                    <label className="brut-label">SEO-Titel</label>
                    <input className="brut-input" value={d.title || ""} onChange={e => setField(p.id, "title", e.target.value)} data-testid={`seo-title-${p.slug}`} />
                  </div>
                  <div>
                    <label className="brut-label">Meta-Description ({(d.description || "").length}/160)</label>
                    <textarea rows={2} className="brut-textarea" value={d.description || ""} onChange={e => setField(p.id, "description", e.target.value)} data-testid={`seo-desc-${p.slug}`} />
                  </div>
                  <div>
                    <label className="brut-label">Keywords</label>
                    <input className="brut-input" value={d.keywords || ""} onChange={e => setField(p.id, "keywords", e.target.value)} />
                  </div>
                  <div>
                    <label className="brut-label">KI-Zusammenfassung (TL;DR für LLMs / GEO)</label>
                    <textarea rows={3} className="brut-textarea" value={d.ai_summary || ""} onChange={e => setField(p.id, "ai_summary", e.target.value)} data-testid={`seo-aisummary-${p.slug}`} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => saveSeo(p.id)} className="brut-btn !py-2 !px-3 text-xs" data-testid={`seo-save-${p.slug}`}><Save size={12} /> Speichern</button>
                    <button onClick={() => gen(p.id, "de")} disabled={busyId === `${p.id}-de`} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs"><Sparkles size={12} /> KI-Vorschlag DE</button>
                    <button onClick={() => gen(p.id, "en")} disabled={busyId === `${p.id}-en`} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs"><Sparkles size={12} /> KI-Vorschlag EN</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListEditor({ label, k, arr, setList, add, del }) {
  return (
    <div>
      <label className="brut-label">{label}</label>
      <div className="space-y-2">
        {arr.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input className="brut-input" value={v} onChange={e => setList(k, i, e.target.value)} />
            <button onClick={() => del(k, i)} className="px-2 text-red-600"><X size={14} /></button>
          </div>
        ))}
        <button onClick={() => add(k)} className="font-mono text-[11px] uppercase underline inline-flex items-center gap-1"><Plus size={11} /> Hinzufügen</button>
      </div>
    </div>
  );
}
